// v5 (not @material-ui/core v4): only v5 reads MuiThemeBridge's palette.mode, so
// a v4 Dialog would render a hardcoded white Paper on a dark page.
import { Dialog } from "@mui/material";
import React, { useState, useEffect } from "react";
import {
  size_jamb_type_fields,
  applied_fields,
  backset_fields,
  bolt_type_fields,
  casing_type_fields,
  core_fields,
  hinge_finish_fields,
  stop_applied_fields,
  stop_fields,
  handing_fields,
} from "../../Forms/SelectValues/values";
import { getPricing } from "../../Forms/Misc/utilities";
import {
  extractNumbers,
  fetchSlabStyles,
  slabStylesForCore,
} from "../utilities";

// Same control look as the Interior Door form this dialog edits.
const selectCls =
  "app-input w-full rounded-xl border px-3 py-2.5 text-sm transition-colors";

const inputCls =
  "app-input w-full rounded-xl border px-3 py-2.5 text-sm transition-colors";

const labelCls = "app-muted mb-1 block text-xs font-medium";
const groupHeadingCls = "app-eyebrow mb-3";

// Strip "(productId)" or "(null)" suffix for display
const cleanValue = (val) => {
  if (typeof val !== "string") return val;
  return val.replace(/\(\d+\)$/, "").replace(/\(null\)$/, "").trim();
};

const DoorBuilderEdit = (props) => {
  const [selectedOptions, setSelectedOptions] = useState({ ...props.rowData });
  const [productIDs, setProductIDs] = useState(extractNumbers(selectedOptions));
  const [pricing, setPricing] = useState(0);
  const [isDoubleDoor, setIsDoubleDoor] = useState(props.rowData.isDouble);
  const [widthOptions, setWidthOptions] = useState([]);
  const [slabStyles, setSlabStyles] = useState([]);

  useEffect(() => {
    fetchSlabStyles().then(setSlabStyles);
  }, []);

  useEffect(() => {
    if (props.rowData) {
      setSelectedOptions({ ...props.rowData });

      const ids = extractNumbers(props.rowData);
      if (isDoubleDoor) {
        getPricing(ids, 1, props.rowData.size_jamb_type, setPricing, props.rowData.ftwayne_prep);
      } else {
        getPricing(ids, 0, props.rowData.size_jamb_type, setPricing, props.rowData.ftwayne_prep);
      }
    }
  }, [props.rowData]);

  useEffect(() => {
    const extractedNumbers = extractNumbers(selectedOptions);
    setProductIDs(extractedNumbers);
  }, [selectedOptions]);

  useEffect(() => {
    if (productIDs.length === 0) return;
    if (isDoubleDoor) {
      getPricing(productIDs, 1, selectedOptions.size_jamb_type, setPricing, selectedOptions.ftwayne_prep);
    } else {
      getPricing(productIDs, 0, selectedOptions.size_jamb_type, setPricing, selectedOptions.ftwayne_prep);
    }
  }, [
    productIDs,
    selectedOptions.quantity,
    selectedOptions.margin,
    selectedOptions.slab_style,
    selectedOptions.ftwayne_prep,
    isDoubleDoor,
  ]);

  const handleDoubleDoorChange = (e) => {
    if (e.target.checked) {
      setSelectedOptions((prev) => ({
        ...prev,
        isDouble: 1,
        handing: "Double",
      }));
      setIsDoubleDoor(true);
    } else {
      setSelectedOptions((prev) => ({
        ...prev,
        isDouble: 0,
      }));
      setIsDoubleDoor(false);
    }
  };

  const handleQuantityChange = (event) => {
    setSelectedOptions((prev) => ({
      ...prev,
      quantity: event.target.value,
    }));
  };

  const handleMarginChange = (event) => {
    setSelectedOptions((prev) => ({
      ...prev,
      margin: event.target.value,
    }));
  };

  useEffect(() => {
    getWidthOptions(selectedOptions.slab_style, selectedOptions.core);
  }, [selectedOptions.slab_style, selectedOptions.core]);

  useEffect(() => {
    if (widthOptions && widthOptions.length > 0 && selectedOptions.width) {
      // Extract just the width portion (e.g. "1'6\"") from the current value
      const currentWidthBase = selectedOptions.width.split(" - ")[0]?.trim();
      const matchedOption = widthOptions.find(
        (option) => option.width === currentWidthBase
      );

      if (matchedOption && matchedOption.productCode) {
        const newValue = `${matchedOption.width} - (${matchedOption.productCode})`;
        if (newValue !== selectedOptions.width) {
          setSelectedOptions((prev) => ({ ...prev, width: newValue }));
        }
      }
    }
  }, [widthOptions]);

  const estimatedPrice = selectedOptions.core === "Bifold"
    ? parseFloat((pricing * selectedOptions.quantity).toFixed(2))
    : Math.round(
        (pricing *
          selectedOptions.quantity *
          (100 / (100 - parseFloat(selectedOptions.margin || 30))) +
          Number.EPSILON) *
          100
      ) / 100;

  const handleSave = () => {
    props.onSaveRowData({
      ...props.rowData,
      ...selectedOptions,
      estimated_price: estimatedPrice,
    });
  };

  const getWidthOptions = async (slab, core) => {
    try {
      const response = await fetch(
        `/api/doors/kits/getSlabOptions?slabStyle=${slab}&coreType=${core}`
      );
      const data = await response.json();

      if (data.message === "Internal Server Error") {
        setWidthOptions([]);
      } else {
        setWidthOptions(data.options);
      }
    } catch (error) {
      console.log(error);
      setWidthOptions([]);
    }
  };

  const handleClose = () => {
    props.setIsEditDialogOpen(false);
    props.setRowData(null);
  };

  const isBifold = selectedOptions.core === "Bifold";
  const isSlabOnly = selectedOptions.size_jamb_type === "Door Slab Only(null)";

  return (
    props.rowData && (
      <Dialog
        open={props.isEditDialogOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <div>
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b app-divider py-3 pl-6 pr-3">
            <h2 className="text-lg font-bold tracking-tight">Edit Door</h2>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="app-icon-btn"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Price banner */}
          {selectedOptions.width && (
            <div className="flex flex-wrap items-baseline justify-center gap-x-2 gap-y-0.5 border-b app-divider bg-emerald-500/10 px-6 py-3">
              <span className="text-sm font-medium">Estimated Price:</span>
              <span className="text-xl font-bold tabular-nums text-emerald-700 dark:text-emerald-300">${estimatedPrice}</span>
              <span className="app-muted text-xs">tax not included</span>
            </div>
          )}

          <div className="px-6 py-5 space-y-5">
            {/* Jamb & Core */}
            {!isBifold && (
              <div>
                <h3 className={groupHeadingCls}>
                  Jamb & Core
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
                  <div>
                    <label className={labelCls}>Jamb Size & Type</label>
                    <select
                      className={selectCls}
                      value={selectedOptions.size_jamb_type || ""}
                      onChange={(e) => {
                        const updates = { size_jamb_type: e.target.value };
                        if (e.target.value.includes("SPLIT")) {
                          updates.stop = null;
                          updates.stop_applied = null;
                        }
                        setSelectedOptions({ ...selectedOptions, ...updates });
                      }}
                    >
                      {size_jamb_type_fields.map((item, index) => {
                        const val = item.description === ""
                          ? ""
                          : `${item.description}(${item.productId})`;
                        return (
                          <option key={index} value={val}>
                            {item.description || "Select Jamb Type..."}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div>
                    <label className={labelCls}>Core</label>
                    <select
                      className={selectCls}
                      value={selectedOptions.core || ""}
                      onChange={(e) =>
                        setSelectedOptions({ ...selectedOptions, core: e.target.value })
                      }
                    >
                      {core_fields.map((item, index) => (
                        <option key={index} value={item}>
                          {index === 0 ? "Select Core..." : `${index}) ${item}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelCls}>Bolt Type</label>
                    <select
                      className={selectCls}
                      value={selectedOptions.bolt_type || ""}
                      onChange={(e) =>
                        setSelectedOptions({ ...selectedOptions, bolt_type: e.target.value })
                      }
                    >
                      {bolt_type_fields(selectedOptions.size_jamb_type, selectedOptions.door_type).map(
                        (item, index) => (
                          <option key={index} value={item}>
                            {index === 0 ? item || "Select..." : `${index}) ${item}`}
                          </option>
                        )
                      )}
                    </select>
                  </div>

                  <div>
                    <label className={labelCls}>Backset</label>
                    <select
                      className={selectCls}
                      value={selectedOptions.backset || ""}
                      onChange={(e) =>
                        setSelectedOptions({ ...selectedOptions, backset: e.target.value })
                      }
                    >
                      {backset_fields(selectedOptions.size_jamb_type, selectedOptions.door_type).map(
                        (item, index) => (
                          <option key={index} value={item}>
                            {index === 0 ? item || "Select..." : `${index}) ${item}`}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Hardware (non-slab-only, non-bifold) */}
            {!isBifold && !isSlabOnly && (
              <div>
                <h3 className={groupHeadingCls}>
                  Hardware
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
                  <div>
                    <label className={labelCls}>Casing Type</label>
                    <select
                      className={selectCls}
                      value={selectedOptions.casing_type || ""}
                      onChange={(e) =>
                        setSelectedOptions({ ...selectedOptions, casing_type: e.target.value })
                      }
                    >
                      {casing_type_fields.map((item, index) => (
                        <option key={index} value={`${item.description}(${item.productId})`}>
                          {item.description || "Select Casing..."}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedOptions.casing_type !== "N/A(null)" && (
                    <div>
                      <label className={labelCls}>Casing Applied</label>
                      <select
                        className={selectCls}
                        value={selectedOptions.applied || ""}
                        onChange={(e) =>
                          setSelectedOptions({ ...selectedOptions, applied: e.target.value })
                        }
                      >
                        {applied_fields.map((item, index) => (
                          <option key={index} value={item}>
                            {index === 0 ? "Select..." : item}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className={labelCls}>Hinge Finish</label>
                    <select
                      className={selectCls}
                      value={selectedOptions.hinge_finish || ""}
                      onChange={(e) =>
                        setSelectedOptions({ ...selectedOptions, hinge_finish: e.target.value })
                      }
                    >
                      {hinge_finish_fields.map((item, index) => (
                        <option key={index} value={`${item.description}(${item.productId})`}>
                          {item.description || "Select Finish..."}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelCls}>Stop</label>
                    <select
                      className={selectCls}
                      value={selectedOptions.stop || ""}
                      onChange={(e) =>
                        setSelectedOptions({ ...selectedOptions, stop: e.target.value })
                      }
                    >
                      {stop_fields(selectedOptions.size_jamb_type).map((item, index) => (
                        <option key={index} value={`${item.description}(${item.productId})`}>
                          {item.description || "Select Stop..."}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelCls}>Stop Applied</label>
                    <select
                      className={selectCls}
                      value={selectedOptions.stop_applied || ""}
                      onChange={(e) =>
                        setSelectedOptions({ ...selectedOptions, stop_applied: e.target.value })
                      }
                    >
                      {stop_applied_fields(selectedOptions.size_jamb_type).map(
                        (item, index) => (
                          <option key={index} value={item}>
                            {index === 0 ? "Select..." : item}
                          </option>
                        )
                      )}
                    </select>
                  </div>

                  <div>
                    <label className={labelCls}>Handing</label>
                    <select
                      className={selectCls}
                      value={selectedOptions.handing || ""}
                      onChange={(e) =>
                        setSelectedOptions({ ...selectedOptions, handing: e.target.value })
                      }
                    >
                      {handing_fields.map((item, index) => (
                        <option key={index} value={item}>
                          {item || "Select..."}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Slab & Width */}
            <div>
              <h3 className={groupHeadingCls}>
                Slab & Size
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
                <div>
                  <label className={labelCls}>Slab Style</label>
                  <select
                    className={selectCls}
                    value={selectedOptions.slab_style || ""}
                    onChange={(e) =>
                      setSelectedOptions({ ...selectedOptions, slab_style: e.target.value, width: null })
                    }
                  >
                    {slabStylesForCore(slabStyles, selectedOptions.core).map(
                      (style) => (
                        <option key={style.name} value={style.name}>
                          {style.name}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div>
                  <label className={labelCls}>Width</label>
                  {selectedOptions.slab_style && widthOptions.length > 0 ? (
                    <select
                      className={selectCls}
                      value={selectedOptions.width || ""}
                      onChange={(e) =>
                        setSelectedOptions({ ...selectedOptions, width: e.target.value })
                      }
                    >
                      <option value="">Select width...</option>
                      {widthOptions.map((option) => {
                        if (option.productCode) {
                          let displayWidth = option.width;
                          if (selectedOptions.isDouble) {
                            const [feet, inches] = option.width
                              .split("'")
                              .map((str) => parseInt(str));
                            const doubleTotalInches = (feet * 12 + inches) * 2;
                            const doubleFeet = Math.floor(doubleTotalInches / 12);
                            const doubleInches = doubleTotalInches % 12;
                            displayWidth = `${doubleFeet}'${doubleInches}"`;
                          }
                          return (
                            <option
                              key={option.width}
                              value={`${option.width} - (${option.productCode})`}
                            >
                              {option.width}
                              {selectedOptions.isDouble ? ` (Actual: ${displayWidth})` : ""}
                            </option>
                          );
                        } else {
                          return (
                            <option key={option.width} disabled value={option.width}>
                              {option.width} - Non Stock
                            </option>
                          );
                        }
                      })}
                    </select>
                  ) : (
                    <div className="flex h-[42px] items-center rounded-xl border border-dashed border-amber-500/50 bg-amber-500/10 px-3">
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-300">Select slab style first</span>
                    </div>
                  )}
                </div>

                {!isBifold && (
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-sky-600"
                        checked={isDoubleDoor || false}
                        onChange={handleDoubleDoorChange}
                      />
                      <span className="text-sm font-medium">Double Door</span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* QTY / Margin / Instructions */}
            <div>
              <h3 className={groupHeadingCls}>
                Quantity & Notes
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <label className={labelCls}>Quantity</label>
                  <input
                    type="number"
                    className={inputCls}
                    value={selectedOptions.quantity || ""}
                    onChange={handleQuantityChange}
                    min={1}
                  />
                </div>
                <div>
                  <label className={labelCls}>Margin (%)</label>
                  <input
                    type="number"
                    className={inputCls}
                    value={selectedOptions.margin || ""}
                    onChange={handleMarginChange}
                    max={99}
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9]/g, ""))
                    }
                  />
                </div>
                <div>
                  <label className={labelCls}>Special Instructions</label>
                  <input
                    type="text"
                    className={inputCls}
                    value={selectedOptions.special_instructions || ""}
                    onChange={(e) =>
                      setSelectedOptions((prev) => ({
                        ...prev,
                        special_instructions: e.target.value,
                      }))
                    }
                    placeholder="Optional notes..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t app-divider px-6 py-4">
            <button
              type="button"
              onClick={handleClose}
              className="app-btn app-btn-ghost"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!selectedOptions.width}
              className="app-btn app-btn-primary"
              onClick={handleSave}
            >
              Save Changes
            </button>
          </div>
        </div>
      </Dialog>
    )
  );
};

export default DoorBuilderEdit;
